import { clientProfile as clientProfileUrls, userInfoUrls } from '@/configs/endpoints'
import api from '@/plugins/api'
import { withPhotoFile, composeFullName, composeAddress } from '@/configs/utils'
import { SUCCESS_CODES } from '@/configs/constants'

const revokeBlobUrls = items => items.forEach(item => item?.file && URL.revokeObjectURL(item.file))

let avatarsPromise = null
let clientProfilePromise = null

export default {
  state: () => ({
    clientProfile: {
      language: localStorage.getItem('lang') || 'ua'
    },
    defaultAvatars: [],
    currentAvatar: null
  }),
  mutations: {
    SET_DATA_PROFILE(state, data) {
      Object.entries(data).forEach(([key, value]) => {
        state[key] = value
      })
    }
  },
  actions: {
    async getClientProfile({ commit, state, getters }) {
      // Dedupe concurrent dispatches (e.g. a redirect chain hitting the router guard twice)
      // so they share one in-flight request instead of double-fetching the profile.
      if (clientProfilePromise) return clientProfilePromise

      clientProfilePromise = (async () => {
        try {
          // The API layer refreshes an expired token before any request, so these are safe to
          // fire together — no need to serialize one request first to absorb the 401.
          const [profileRes, settingsRes, oauthRes] = await Promise.all([
            api.get(clientProfileUrls.profile),
            api.get(clientProfileUrls.settings),
            api.get(userInfoUrls.oauthGoogle)
          ])
          const profileData = SUCCESS_CODES.includes(profileRes.code) ? profileRes.data : {}
          const settingsData = SUCCESS_CODES.includes(settingsRes.code) ? settingsRes.data : {}
          const oauthEmail = oauthRes.data?.email || null

          commit('SET_DATA_PROFILE', {
            clientProfile: {
              ...state.clientProfile,
              ...profileData,
              language: settingsData.language || state.clientProfile.language
            }
          })
          commit('SET_DATA_USER', { oauthGoogleEmail: oauthEmail })

          if (!getters.isAdmin() && profileData.login) {
            window.dashly?.identify({
              Name: composeFullName(profileData),
              Email: oauthEmail,
              login: profileData.login,
              'User ID': profileData.login,
              address: composeAddress(profileData)
            })
          }
        } catch (error) {
          console.error('Failed to get client profile:', error)
        } finally {
          clientProfilePromise = null
        }
      })()

      return clientProfilePromise
    },
    async patchClientSettings({ commit, state }, payload) {
      try {
        const response = await api.patch(clientProfileUrls.settings, payload)
        if (SUCCESS_CODES.includes(response.code))
          commit('SET_DATA_PROFILE', {
            clientProfile: { ...state.clientProfile, language: response.data.language }
          })
        return response
      } catch (error) {
        console.error('Failed to patch client settings:', error)
      }
    },
    async getDefaultAvatars({ commit, state }) {
      if (state.defaultAvatars.length) return
      if (avatarsPromise) return avatarsPromise

      avatarsPromise = (async () => {
        try {
          // `avatars/all` is the selectable catalog; `avatars` returns the client's own avatars,
          // the first item being the currently active one.
          const [catalogRes, currentRes] = await Promise.all([
            api.get(clientProfileUrls.avatars),
            api.get(clientProfileUrls.clientAvatar)
          ])
          revokeBlobUrls(state.defaultAvatars)
          // The catalog and the active avatar are independent: resolve the active one even when the
          // selectable catalog comes back empty, so an existing avatar still displays.
          const items = (await Promise.all((catalogRes.data ?? []).map(withPhotoFile))).filter(Boolean)
          const currentAvatar = await withPhotoFile(currentRes.data?.[0])
          commit('SET_DATA_PROFILE', { defaultAvatars: items, currentAvatar })
        } catch (error) {
          console.error('Failed to get default avatars:', error)
        } finally {
          avatarsPromise = null
        }
      })()

      return avatarsPromise
    },
    async sendAvatar({ commit, state }, payload) {
      try {
        const response = await api.post(clientProfileUrls.clientAvatar, payload)
        const chosen = state.defaultAvatars.find(a => a.name === payload?.body?.name)
        if (SUCCESS_CODES.includes(response.code) && chosen) commit('SET_DATA_PROFILE', { currentAvatar: chosen })
        return response
      } catch (error) {
        console.error('Failed to send avatar:', error)
      }
    }
  },
  getters: {
    isAuthed: state => !!state.clientProfile.login,
    fullName: state => composeFullName(state.clientProfile)
  }
}
